/** Deterministic rich-state people client for demo mode and Vitest. */

import type {
  AliasSuggestion,
  ApiEnvelope,
  Person,
  PersonAlias,
  PersonMentionToken,
  ProviderAccountCandidate,
} from '@zunelo/shared';
import { simulateNetwork } from './mockClientUtils';
import type { ClientConfig, IPeopleClient } from './types';

const timestamp = '2026-09-08T00:00:00.000Z';
const alias = (platform: PersonAlias['platform'], id: string, username?: string): PersonAlias => ({
  id: `${platform}-${id}`,
  platform,
  externalId: id,
  displayName: platform === 'slack' ? 'Ada Lovelace' : 'Ada Lovelace',
  ...(username ? { username } : {}),
  refreshedAt: timestamp,
});

const initialPeople = (): Person[] => [
  {
    id: 'person-ada',
    ownerUid: 'mock-user',
    displayName: 'Ada Lovelace',
    aliases: [alias('slack', 'UADA'), alias('github', '100', 'ada-lovelace')],
    preferredAliasIds: { slack: 'slack-UADA', github: 'github-100' },
    revision: 2,
    createdAt: timestamp,
    updatedAt: timestamp,
  },
  {
    id: 'person-grace',
    ownerUid: 'mock-user',
    displayName: 'Grace Hopper',
    aliases: [],
    revision: 1,
    createdAt: timestamp,
    updatedAt: timestamp,
  },
];

const providerCandidates = (): ProviderAccountCandidate[] => [
  {
    platform: 'slack',
    externalId: 'UADA',
    username: 'ada-lovelace',
    displayName: 'Ada Lovelace',
    refreshedAt: timestamp,
  },
  {
    platform: 'slack',
    externalId: 'UGRACE',
    username: 'grace-hopper',
    displayName: 'Grace Hopper',
    refreshedAt: timestamp,
  },
  {
    platform: 'jira',
    externalId: 'jira-grace',
    username: 'grace.hopper',
    displayName: 'Grace Hopper',
    refreshedAt: timestamp,
  },
  {
    platform: 'github',
    externalId: '100',
    username: 'ada-lovelace',
    displayName: 'Ada Lovelace',
    refreshedAt: timestamp,
  },
  {
    platform: 'github',
    externalId: '101',
    username: 'grace-hopper',
    displayName: 'Grace Hopper',
    refreshedAt: timestamp,
  },
];

const initialSuggestions = (): AliasSuggestion[] => [
  {
    id: 'suggestion-grace-slack',
    ownerUid: 'mock-user',
    personId: 'person-grace',
    candidate: { ...providerCandidates()[1], id: 'alias-slack-UGRACE' },
    evidence: { kind: 'manual_search' },
    status: 'pending',
    expiresAt: '2026-10-08T00:00:00.000Z',
    createdAt: timestamp,
    updatedAt: timestamp,
  },
];

function aliasToCandidate(item: PersonAlias): ProviderAccountCandidate {
  return {
    platform: item.platform,
    externalId: item.externalId,
    displayName: item.displayName,
    refreshedAt: item.refreshedAt,
    ...(item.username ? { username: item.username } : {}),
    ...(item.email ? { email: item.email } : {}),
    ...(item.avatarUrl ? { avatarUrl: item.avatarUrl } : {}),
    ...(item.profileUrl ? { profileUrl: item.profileUrl } : {}),
  };
}

export class MockPeopleClient implements IPeopleClient {
  private readonly config: ClientConfig;
  private people = initialPeople();
  private suggestions = initialSuggestions();

  constructor(config: ClientConfig) {
    this.config = config;
  }

  private run<T>(fn: () => T): Promise<ApiEnvelope<T>> {
    return simulateNetwork(fn, this.config);
  }

  listPeople(query = '') {
    return this.run(() => {
      const needle = query.trim().toLowerCase();
      return this.people
        .filter((person) => {
          if (!needle) return true;
          const searchable = [
            person.displayName,
            ...person.aliases.flatMap((item) => [
              item.displayName,
              item.username ?? '',
              item.externalId,
            ]),
          ]
            .join(' ')
            .toLowerCase();
          return searchable.includes(needle);
        })
        .map((person) => structuredClone(person));
    });
  }
  getPerson(personId: string) {
    return this.run(() => this.find(personId));
  }
  createPerson(input: { displayName: string; avatarUrl?: string }) {
    return this.run(() => {
      const t = new Date().toISOString();
      const person: Person = {
        id: `person-${this.people.length + 1}`,
        ownerUid: 'mock-user',
        displayName: input.displayName,
        aliases: [],
        revision: 1,
        createdAt: t,
        updatedAt: t,
        ...(input.avatarUrl ? { avatarUrl: input.avatarUrl } : {}),
      };
      this.people.push(person);
      return structuredClone(person);
    });
  }
  updatePerson(
    personId: string,
    input: { displayName?: string; avatarUrl?: string; expectedRevision: number },
  ) {
    return this.run(() => {
      const person = this.mutate(personId, input.expectedRevision);
      const next = {
        ...person,
        ...(input.displayName === undefined ? {} : { displayName: input.displayName }),
        ...(input.avatarUrl === undefined ? {} : { avatarUrl: input.avatarUrl }),
        revision: person.revision + 1,
      };
      this.replace(next);
      return structuredClone(next);
    });
  }
  attachAlias(personId: string, candidate: ProviderAccountCandidate, expectedRevision: number) {
    return this.run(() => {
      const person = this.mutate(personId, expectedRevision);
      const externalId = candidate.externalId.trim();
      const displayName = candidate.displayName.trim();
      if (!externalId || !displayName) throw new Error('PERSON_INVALID_ALIAS');
      if (
        this.people.some((item) =>
          item.aliases.some(
            (alias) => alias.platform === candidate.platform && alias.externalId === externalId,
          ),
        )
      ) {
        throw new Error('PERSON_ALIAS_CLAIMED');
      }
      const nextAlias: PersonAlias = {
        ...candidate,
        externalId,
        displayName,
        id: `alias-${candidate.platform}-${externalId}`,
        refreshedAt: candidate.refreshedAt || timestamp,
      };
      const next = {
        ...person,
        aliases: [...person.aliases, nextAlias],
        preferredAliasIds: {
          ...(person.preferredAliasIds ?? {}),
          [candidate.platform]: nextAlias.id,
        },
        revision: person.revision + 1,
      };
      this.replace(next);
      return structuredClone(next);
    });
  }
  detachAlias(personId: string, aliasId: string, expectedRevision: number) {
    return this.run(() => {
      const person = this.mutate(personId, expectedRevision);
      if (!person.aliases.some((item) => item.id === aliasId)) {
        throw new Error('PERSON_ALIAS_NOT_FOUND');
      }
      const aliases = person.aliases.filter((item) => item.id !== aliasId);
      const next = { ...person, aliases, revision: person.revision + 1 };
      this.replace(next);
      return structuredClone(next);
    });
  }
  setPreferredAlias(
    personId: string,
    platform: PersonAlias['platform'],
    aliasId: string,
    expectedRevision: number,
  ) {
    return this.run(() => {
      const person = this.mutate(personId, expectedRevision);
      const item = person.aliases.find(
        (candidate) => candidate.id === aliasId && candidate.platform === platform,
      );
      if (!item) throw new Error('PERSON_ALIAS_NOT_FOUND');
      const next = {
        ...person,
        preferredAliasIds: { ...(person.preferredAliasIds ?? {}), [platform]: aliasId },
        revision: person.revision + 1,
      };
      this.replace(next);
      return structuredClone(next);
    });
  }
  deletePerson(personId: string, expectedRevision: number) {
    return this.run(() => {
      this.mutate(personId, expectedRevision);
      this.people = this.people.filter((person) => person.id !== personId);
      this.suggestions = this.suggestions.filter((suggestion) => suggestion.personId !== personId);
      return { deleted: true };
    });
  }
  mergePeople(targetPersonId: string, sourcePersonIds: string[], expectedRevision: number) {
    return this.run(() => {
      const target = this.mutate(targetPersonId, expectedRevision);
      const sources = sourcePersonIds.map((id) => this.find(id));
      const aliases = [...target.aliases, ...sources.flatMap((person) => person.aliases)];
      const next = { ...target, aliases, revision: target.revision + 1 };
      this.people = this.people
        .filter((person) => person.id !== targetPersonId && !sourcePersonIds.includes(person.id))
        .concat(next);
      return structuredClone(next);
    });
  }
  splitPerson(
    sourcePersonId: string,
    aliasIds: string[],
    expectedRevision: number,
    displayName?: string,
  ) {
    return this.run(() => {
      const source = this.mutate(sourcePersonId, expectedRevision);
      const moved = source.aliases.filter((item) => aliasIds.includes(item.id));
      const target: Person = {
        id: `person-split-${this.people.length + 1}`,
        ownerUid: source.ownerUid,
        displayName: displayName?.trim() || `${source.displayName} (split)`,
        aliases: moved,
        revision: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      this.replace({
        ...source,
        aliases: source.aliases.filter((item) => !aliasIds.includes(item.id)),
        revision: source.revision + 1,
      });
      this.people.push(target);
      return structuredClone(target);
    });
  }
  reassignAlias(
    targetPersonId: string,
    aliasId: string,
    sourcePersonId: string,
    expectedRevision: number,
  ) {
    return this.run(() => {
      const target = this.mutate(targetPersonId, expectedRevision);
      const source = this.find(sourcePersonId);
      const item = source.aliases.find((alias) => alias.id === aliasId);
      if (!item) throw new Error('Alias not found');
      const next = { ...target, aliases: [...target.aliases, item], revision: target.revision + 1 };
      this.replace(next);
      this.replace({
        ...source,
        aliases: source.aliases.filter((alias) => alias.id !== aliasId),
        revision: source.revision + 1,
      });
      return structuredClone(next);
    });
  }
  listSuggestions(): Promise<ApiEnvelope<AliasSuggestion[]>> {
    return this.run(() =>
      structuredClone(this.suggestions.filter((item) => item.status === 'pending')),
    );
  }
  acceptSuggestion(
    suggestionId: string,
    expectedRevision: number,
    candidateExternalId?: string,
  ): Promise<ApiEnvelope<Person>> {
    return this.run(() => {
      const suggestion = this.suggestions.find(
        (item) => item.id === suggestionId && item.status === 'pending',
      );
      if (!suggestion) throw new Error('PERSON_SUGGESTION_NOT_FOUND');
      if (candidateExternalId && candidateExternalId !== suggestion.candidate.externalId) {
        throw new Error('PERSON_SUGGESTION_NOT_FOUND');
      }
      const person = this.mutate(suggestion.personId, expectedRevision);
      const alreadyAttached = person.aliases.some(
        (alias) =>
          alias.platform === suggestion.candidate.platform &&
          alias.externalId === suggestion.candidate.externalId,
      );
      const next = alreadyAttached
        ? person
        : {
            ...person,
            aliases: [
              ...person.aliases,
              {
                ...suggestion.candidate,
                id: `alias-${suggestion.candidate.platform}-${suggestion.candidate.externalId}`,
              },
            ],
            preferredAliasIds: {
              ...(person.preferredAliasIds ?? {}),
              [suggestion.candidate.platform]:
                `alias-${suggestion.candidate.platform}-${suggestion.candidate.externalId}`,
            },
            revision: person.revision + 1,
          };
      this.replace(next);
      this.suggestions = this.suggestions.filter((item) => item.id !== suggestionId);
      return structuredClone(next);
    });
  }
  rejectSuggestion(suggestionId: string): Promise<ApiEnvelope<{ rejected: boolean }>> {
    return this.run(() => {
      this.suggestions = this.suggestions.filter((item) => item.id !== suggestionId);
      return { rejected: true };
    });
  }
  resolveMention(token: PersonMentionToken) {
    return this.run(() => {
      const person = this.find(token.personId);
      const item = person.aliases.find(
        (alias) => alias.id === token.aliasId && alias.platform === token.platform,
      );
      if (!item) throw new Error('Mention alias is stale.');
      return structuredClone(item);
    });
  }
  exportPeople() {
    return this.run(() => ({ people: structuredClone(this.people) }));
  }
  searchProviderAccounts(platform: PersonAlias['platform'], query: string) {
    return this.run(() => {
      const known = new Map<string, ProviderAccountCandidate>();
      for (const candidate of providerCandidates()) {
        known.set(`${candidate.platform}:${candidate.externalId}`, candidate);
      }
      for (const person of this.people) {
        for (const item of person.aliases) {
          const candidate = aliasToCandidate(item);
          known.set(`${candidate.platform}:${candidate.externalId}`, candidate);
        }
      }
      const normalizedQuery = query.toLowerCase();
      return [...known.values()].filter(
        (item) =>
          item.platform === platform &&
          `${item.displayName} ${item.username ?? ''} ${item.email ?? ''} ${item.externalId}`
            .toLowerCase()
            .includes(normalizedQuery),
      );
    });
  }

  private find(id: string): Person {
    const person = this.people.find((item) => item.id === id);
    if (!person) throw new Error(`Person ${id} was not found.`);
    return structuredClone(person);
  }
  private mutate(id: string, expectedRevision: number): Person {
    const person = this.people.find((item) => item.id === id);
    if (!person) throw new Error(`Person ${id} was not found.`);
    if (person.revision !== expectedRevision) throw new Error('PERSON_REVISION_CONFLICT');
    return structuredClone(person);
  }
  private replace(person: Person): void {
    this.people = this.people.map((item) => (item.id === person.id ? person : item));
  }
}
